import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';

/**
 * SmartfarmRequest.jsx
 *
 * 연결 API
 * GET  /api/member/smartfarm/request-status
 * POST /api/member/smartfarm/request
 * POST /api/member/smartfarm/request-cancel
 *
 * 라우터 예시
 * <Route path="/smartfarm/request" element={<SmartfarmRequest />} />
 */
const SmartfarmRequest = () => {
  const navigate = useNavigate();

  const api = useMemo(
    () =>
      axios.create({
        withCredentials: true,
      }),
    []
  );

  const [requestData, setRequestData] = useState(null);
  const [form, setForm] = useState({
    farm_name: '',
    farm_type: '',
    farm_address: '',
    facility_size: '',
    desired_features: [],
    install_environment: '',
    memo: '',
  });

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [canceling, setCanceling] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  const featureOptions = [
    {
      value: 'temperature_humidity',
      label: '온도·습도 확인',
      description: '농장 온도와 습도를 실시간으로 확인합니다.',
    },
    {
      value: 'soil_moisture',
      label: '토양 수분 확인',
      description: '토양 수분 센서 상태를 확인합니다.',
    },
    {
      value: 'irrigation',
      label: '관수 펌프 제어',
      description: '휴대폰에서 관수 펌프를 켜고 끕니다.',
    },
    {
      value: 'ventilation',
      label: '환풍기 제어',
      description: '온도와 습도에 따라 환풍기를 제어합니다.',
    },
    {
      value: 'lighting',
      label: '조명 제어',
      description: '농장 조명이나 재배등을 제어합니다.',
    },
    {
      value: 'automation',
      label: '자동화 설정',
      description: '센서 조건에 따라 장치를 자동으로 작동합니다.',
    },
    {
      value: 'cctv',
      label: 'CCTV 확인',
      description: '농장 카메라 화면을 원격으로 확인합니다.',
    },
    {
      value: 'other',
      label: '기타 장치',
      description: '추가로 필요한 센서나 장치가 있습니다.',
    },
  ];

  const formatDateTime = (value) => {
    if (!value) return '-';

    const date = new Date(value);

    if (Number.isNaN(date.getTime())) {
      return String(value);
    }

    return new Intl.DateTimeFormat('ko-KR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    }).format(date);
  };

  const getStatusText = (status) => {
    const labels = {
      REQUEST: '신청 접수',
      SETTING: '설치 준비 중',
      COMPLETE: '설치 완료',
      CANCEL: '신청 취소',
      REJECT: '신청 반려',
    };

    return labels[status] || status || '신청 내역 없음';
  };

  const getStatusBadgeClass = (status) => {
    if (status === 'COMPLETE') return 'text-bg-success';
    if (status === 'SETTING') return 'text-bg-warning';
    if (status === 'REQUEST') return 'text-bg-primary';
    if (status === 'REJECT') return 'text-bg-danger';
    return 'text-bg-secondary';
  };

  const handleApiError = useCallback(
    (err, fallbackMessage) => {
      const status = err?.response?.status;
      const redirect = err?.response?.data?.redirect;
      const message =
        err?.response?.data?.error ||
        err?.message ||
        fallbackMessage;

      if (status === 401) {
        navigate(redirect || '/login', { replace: true });
        return;
      }

      setError(message);
    },
    [navigate]
  );

  const loadRequestStatus = useCallback(async () => {
    setLoading(true);
    setError('');

    try {
      const response = await api.get(
        '/api/member/smartfarm/request-status'
      );

      const data = response.data || {};

      if (!data.ok) {
        throw new Error(
          data.error || '스마트팜 신청 상태를 불러오지 못했습니다.'
        );
      }

      setRequestData(data);
    } catch (err) {
      handleApiError(
        err,
        '스마트팜 신청 상태를 불러오지 못했습니다.'
      );
    } finally {
      setLoading(false);
    }
  }, [api, handleApiError]);

  useEffect(() => {
    loadRequestStatus();
  }, [loadRequestStatus]);

  const handleChange = (event) => {
    const { name, value } = event.target;

    setForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleFeatureToggle = (value) => {
    setForm((prev) => {
      const exists = prev.desired_features.includes(value);

      return {
        ...prev,
        desired_features: exists
          ? prev.desired_features.filter((item) => item !== value)
          : [...prev.desired_features, value],
      };
    });
  };

  const buildMemo = () => {
    const selectedFeatureLabels = featureOptions
      .filter((option) =>
        form.desired_features.includes(option.value)
      )
      .map((option) => option.label);

    const lines = [
      form.farm_name ? `농장명: ${form.farm_name.trim()}` : '',
      form.farm_type ? `농장 형태: ${form.farm_type}` : '',
      form.farm_address
        ? `설치 지역: ${form.farm_address.trim()}`
        : '',
      form.facility_size
        ? `시설 규모: ${form.facility_size.trim()}`
        : '',
      selectedFeatureLabels.length
        ? `희망 기능: ${selectedFeatureLabels.join(', ')}`
        : '',
      form.install_environment
        ? `설치 환경: ${form.install_environment.trim()}`
        : '',
      form.memo ? `추가 요청사항: ${form.memo.trim()}` : '',
    ].filter(Boolean);

    return lines.join('\n');
  };

  const validateForm = () => {
    if (!form.farm_name.trim()) {
      setError('농장명을 입력하세요.');
      return false;
    }

    if (!form.farm_type) {
      setError('농장 형태를 선택하세요.');
      return false;
    }

    if (!form.farm_address.trim()) {
      setError('설치 지역을 입력하세요.');
      return false;
    }

    if (form.desired_features.length === 0) {
      setError('희망 기능을 하나 이상 선택하세요.');
      return false;
    }

    return true;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    setError('');
    setNotice('');

    if (!validateForm()) return;

    setSubmitting(true);

    try {
      const response = await api.post(
        '/api/member/smartfarm/request',
        {
          memo: buildMemo(),
        }
      );

      const data = response.data || {};

      if (!data.ok) {
        throw new Error(
          data.error || '스마트팜 신청에 실패했습니다.'
        );
      }

      setNotice(
        data.message || '스마트팜 신청이 접수되었습니다.'
      );

      setForm({
        farm_name: '',
        farm_type: '',
        farm_address: '',
        facility_size: '',
        desired_features: [],
        install_environment: '',
        memo: '',
      });

      await loadRequestStatus();
    } catch (err) {
      handleApiError(err, '스마트팜 신청에 실패했습니다.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = async () => {
    const requestId = requestData?.request?.id;

    if (!requestId || canceling) return;

    if (!window.confirm('접수된 스마트팜 신청을 취소하시겠습니까?')) {
      return;
    }

    setCanceling(true);
    setError('');
    setNotice('');

    try {
      const response = await api.post(
        '/api/member/smartfarm/request-cancel',
        {
          request_id: requestId,
        }
      );

      const data = response.data || {};

      if (!data.ok) {
        throw new Error(
          data.error || '스마트팜 신청 취소에 실패했습니다.'
        );
      }

      setNotice(
        data.message || '스마트팜 신청이 취소되었습니다.'
      );

      await loadRequestStatus();
    } catch (err) {
      handleApiError(
        err,
        '스마트팜 신청 취소에 실패했습니다.'
      );
    } finally {
      setCanceling(false);
    }
  };

  const renderProgress = (status) => {
    const steps = [
      {
        key: 'REQUEST',
        label: '신청 접수',
        description: '신청 내용을 확인합니다.',
      },
      {
        key: 'SETTING',
        label: '설치 준비',
        description: '장비와 설치 환경을 준비합니다.',
      },
      {
        key: 'COMPLETE',
        label: '설치 완료',
        description: '스마트팜 서비스를 사용할 수 있습니다.',
      },
    ];

    const statusOrder = {
      REQUEST: 0,
      SETTING: 1,
      COMPLETE: 2,
    };

    const currentIndex = statusOrder[status] ?? -1;

    return (
      <div className="row g-3">
        {steps.map((step, index) => {
          const completed = currentIndex >= index;
          const current = currentIndex === index;

          return (
            <div
              key={step.key}
              className="col-12 col-md-4"
            >
              <div
                className={`border rounded-3 p-3 h-100 ${
                  current
                    ? 'border-primary bg-primary-subtle'
                    : completed
                      ? 'border-success bg-success-subtle'
                      : 'bg-light'
                }`}
              >
                <div className="d-flex align-items-center gap-3">
                  <div
                    className={`rounded-circle d-flex align-items-center justify-content-center flex-shrink-0 ${
                      current
                        ? 'bg-primary text-white'
                        : completed
                          ? 'bg-success text-white'
                          : 'bg-secondary-subtle text-secondary'
                    }`}
                    style={{
                      width: 38,
                      height: 38,
                    }}
                  >
                    {completed ? '✓' : index + 1}
                  </div>

                  <div>
                    <div className="fw-bold">
                      {step.label}
                    </div>
                    <div className="small text-secondary">
                      {step.description}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="py-5 text-center">
        <div
          className="spinner-border text-primary"
          role="status"
          aria-label="로딩 중"
        />
        <div className="mt-3 text-secondary">
          스마트팜 신청 상태를 불러오고 있습니다.
        </div>
      </div>
    );
  }

  const currentRequest = requestData?.request;
  const hasActiveRequest =
    currentRequest &&
    ['REQUEST', 'SETTING'].includes(currentRequest.status);

  const canSubmit =
    !currentRequest ||
    ['CANCEL', 'REJECT'].includes(currentRequest.status);

  const isComplete = currentRequest?.status === 'COMPLETE';

  return (
    <div className="container-fluid px-0">
      <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-3 mb-4">
        <div>
          <h2 className="fw-bold mb-1">스마트팜 신청</h2>
          <p className="text-secondary mb-0">
            농장 환경과 필요한 기능을 알려주시면 설치 구성을 확인합니다.
          </p>
        </div>

        <button
          type="button"
          className="btn btn-outline-secondary text-nowrap"
          onClick={() => navigate('/smartfarm')}
        >
          스마트팜으로
        </button>
      </div>

      {error && (
        <div
          className="alert alert-danger d-flex justify-content-between align-items-center"
          role="alert"
        >
          <span>{error}</span>

          <button
            type="button"
            className="btn-close"
            aria-label="닫기"
            onClick={() => setError('')}
          />
        </div>
      )}

      {notice && (
        <div
          className="alert alert-success d-flex justify-content-between align-items-center"
          role="alert"
        >
          <span>{notice}</span>

          <button
            type="button"
            className="btn-close"
            aria-label="닫기"
            onClick={() => setNotice('')}
          />
        </div>
      )}

      {currentRequest && (
        <div className="card border-0 shadow-sm mb-4">
          <div className="card-header bg-white border-0 py-3 d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-2">
            <div>
              <div className="fw-bold">현재 신청 상태</div>
              <div className="small text-secondary mt-1">
                신청 처리 상태와 진행 단계를 확인할 수 있습니다.
              </div>
            </div>

            <span
              className={`badge rounded-pill fs-6 ${getStatusBadgeClass(
                currentRequest.status
              )}`}
            >
              {getStatusText(currentRequest.status)}
            </span>
          </div>

          <div className="card-body">
            {!['CANCEL', 'REJECT'].includes(
              currentRequest.status
            ) &&
              renderProgress(currentRequest.status)}

            <div className="border rounded p-3 mt-4">
              <dl className="row mb-0">
                <dt className="col-4 col-md-2 text-secondary fw-normal">
                  신청번호
                </dt>
                <dd className="col-8 col-md-4">
                  {currentRequest.id || '-'}
                </dd>

                <dt className="col-4 col-md-2 text-secondary fw-normal">
                  신청일
                </dt>
                <dd className="col-8 col-md-4">
                  {formatDateTime(currentRequest.created_at)}
                </dd>

                <dt className="col-4 col-md-2 text-secondary fw-normal">
                  최근 수정
                </dt>
                <dd className="col-8 col-md-4">
                  {formatDateTime(
                    currentRequest.updated_at ||
                      currentRequest.created_at
                  )}
                </dd>

                <dt className="col-4 col-md-2 text-secondary fw-normal">
                  처리 상태
                </dt>
                <dd className="col-8 col-md-4">
                  {getStatusText(currentRequest.status)}
                </dd>
              </dl>

              {currentRequest.memo && (
                <div className="mt-3 pt-3 border-top">
                  <div className="small text-secondary mb-2">
                    신청 내용
                  </div>

                  <div
                    className="bg-light rounded p-3"
                    style={{
                      whiteSpace: 'pre-wrap',
                    }}
                  >
                    {currentRequest.memo}
                  </div>
                </div>
              )}

              {currentRequest.admin_memo && (
                <div className="mt-3 pt-3 border-top">
                  <div className="small text-secondary mb-2">
                    관리자 안내
                  </div>

                  <div
                    className="alert alert-info mb-0"
                    style={{
                      whiteSpace: 'pre-wrap',
                    }}
                  >
                    {currentRequest.admin_memo}
                  </div>
                </div>
              )}

              {currentRequest.status === 'REQUEST' && (
                <div className="mt-3 pt-3 border-top text-end">
                  <button
                    type="button"
                    className="btn btn-outline-danger"
                    onClick={handleCancel}
                    disabled={canceling}
                  >
                    {canceling
                      ? '취소 처리 중...'
                      : '신청 취소'}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {isComplete && (
        <div className="card border-0 shadow-sm mb-4">
          <div className="card-body p-4 text-center">
            <div
              className="rounded-circle bg-success-subtle d-flex align-items-center justify-content-center mx-auto mb-3"
              style={{
                width: 72,
                height: 72,
                fontSize: 34,
              }}
            >
              ✓
            </div>

            <h3 className="h4 fw-bold">설치가 완료되었습니다</h3>
            <p className="text-secondary">
              이제 스마트팜 화면에서 농장 환경을 확인하고 장치를 제어할 수
              있습니다.
            </p>

            <button
              type="button"
              className="btn btn-primary"
              onClick={() => navigate('/smartfarm')}
            >
              스마트팜 열기
            </button>
          </div>
        </div>
      )}

      {hasActiveRequest && (
        <div className="alert alert-info">
          현재 신청이 처리 중입니다. 설치 환경 확인이나 추가 정보가 필요한
          경우 관리자가 별도로 안내합니다.
        </div>
      )}

      {canSubmit && (
        <form onSubmit={handleSubmit}>
          <div className="card border-0 shadow-sm mb-4">
            <div className="card-header bg-white border-0 py-3">
              <div className="fw-bold">농장 기본 정보</div>
              <div className="small text-secondary mt-1">
                설치할 농장의 기본 정보를 입력합니다.
              </div>
            </div>

            <div className="card-body">
              <div className="row g-4">
                <div className="col-12 col-md-6">
                  <label
                    htmlFor="farm_name"
                    className="form-label fw-semibold"
                  >
                    농장명 <span className="text-danger">*</span>
                  </label>

                  <input
                    id="farm_name"
                    name="farm_name"
                    type="text"
                    className="form-control"
                    value={form.farm_name}
                    onChange={handleChange}
                    maxLength={100}
                    placeholder="예: 콩꼬물 딸기농장"
                    required
                  />
                </div>

                <div className="col-12 col-md-6">
                  <label
                    htmlFor="farm_type"
                    className="form-label fw-semibold"
                  >
                    농장 형태 <span className="text-danger">*</span>
                  </label>

                  <select
                    id="farm_type"
                    name="farm_type"
                    className="form-select"
                    value={form.farm_type}
                    onChange={handleChange}
                    required
                  >
                    <option value="">농장 형태 선택</option>
                    <option value="비닐하우스">비닐하우스</option>
                    <option value="유리온실">유리온실</option>
                    <option value="노지">노지</option>
                    <option value="축사">축사</option>
                    <option value="실내 재배">실내 재배</option>
                    <option value="기타">기타</option>
                  </select>
                </div>

                <div className="col-12 col-md-8">
                  <label
                    htmlFor="farm_address"
                    className="form-label fw-semibold"
                  >
                    설치 지역 <span className="text-danger">*</span>
                  </label>

                  <input
                    id="farm_address"
                    name="farm_address"
                    type="text"
                    className="form-control"
                    value={form.farm_address}
                    onChange={handleChange}
                    maxLength={255}
                    placeholder="예: 전북특별자치도 전주시"
                    required
                  />
                </div>

                <div className="col-12 col-md-4">
                  <label
                    htmlFor="facility_size"
                    className="form-label fw-semibold"
                  >
                    시설 규모
                  </label>

                  <input
                    id="facility_size"
                    name="facility_size"
                    type="text"
                    className="form-control"
                    value={form.facility_size}
                    onChange={handleChange}
                    maxLength={100}
                    placeholder="예: 비닐하우스 2동"
                  />
                </div>

                <div className="col-12">
                  <label
                    htmlFor="install_environment"
                    className="form-label fw-semibold"
                  >
                    설치 환경
                  </label>

                  <textarea
                    id="install_environment"
                    name="install_environment"
                    className="form-control"
                    rows={4}
                    value={form.install_environment}
                    onChange={handleChange}
                    maxLength={1000}
                    placeholder="인터넷 설치 여부, 전원 위치, 장치 설치 장소 등을 적어주세요."
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="card border-0 shadow-sm mb-4">
            <div className="card-header bg-white border-0 py-3">
              <div className="fw-bold">
                필요한 스마트팜 기능{' '}
                <span className="text-danger">*</span>
              </div>
              <div className="small text-secondary mt-1">
                농장에 필요한 기능을 모두 선택하세요.
              </div>
            </div>

            <div className="card-body">
              <div className="row g-3">
                {featureOptions.map((option) => {
                  const checked = form.desired_features.includes(
                    option.value
                  );

                  return (
                    <div
                      key={option.value}
                      className="col-12 col-md-6 col-xl-4"
                    >
                      <label
                        className={`border rounded-3 p-3 h-100 d-block ${
                          checked
                            ? 'border-primary bg-primary-subtle'
                            : ''
                        }`}
                        style={{
                          cursor: 'pointer',
                        }}
                      >
                        <div className="d-flex align-items-start gap-3">
                          <input
                            className="form-check-input mt-1"
                            type="checkbox"
                            checked={checked}
                            onChange={() =>
                              handleFeatureToggle(option.value)
                            }
                          />

                          <div>
                            <div className="fw-bold mb-1">
                              {option.label}
                            </div>
                            <div className="small text-secondary">
                              {option.description}
                            </div>
                          </div>
                        </div>
                      </label>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          <div className="card border-0 shadow-sm mb-4">
            <div className="card-header bg-white border-0 py-3">
              <div className="fw-bold">추가 요청사항</div>
              <div className="small text-secondary mt-1">
                설치 담당자가 확인해야 할 내용을 적어주세요.
              </div>
            </div>

            <div className="card-body">
              <textarea
                name="memo"
                className="form-control"
                rows={6}
                value={form.memo}
                onChange={handleChange}
                maxLength={2000}
                placeholder="예: 관수 펌프는 기존 220V 펌프를 사용하고 싶습니다. 야외 설치라 방수함이 필요합니다."
              />

              <div className="form-text text-end">
                {form.memo.length}/2000
              </div>
            </div>
          </div>

          <div className="d-flex flex-column flex-sm-row justify-content-end gap-2 pb-4">
            <button
              type="button"
              className="btn btn-outline-secondary"
              onClick={() => navigate('/smartfarm')}
            >
              취소
            </button>

            <button
              type="submit"
              className="btn btn-primary px-4"
              disabled={submitting}
            >
              {submitting ? '신청 중...' : '스마트팜 신청'}
            </button>
          </div>
        </form>
      )}
    </div>
  );
};

export default SmartfarmRequest;